import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Container, 
  Alert,
  CircularProgress,
  Divider,
  Stack
} from '@mui/material';
import useSubscription from '../hooks/useSubscription';
import { useCompany } from '../hooks/useCompany';
import { useUser } from '@clerk/clerk-react';
import LockIcon from '@mui/icons-material/Lock';
import StarsIcon from '@mui/icons-material/Stars';

type RequireSubscriptionProps = {
  children: React.ReactNode;
  allowOneFreeForm?: boolean;
  featureId?: string;
  useFreePage?: boolean;
};

// This variable ensures we only show one alert across all instances
const alertShownMap = new Map<string, boolean>();

const RequireSubscription: React.FC<RequireSubscriptionProps> = ({ 
  children, 
  allowOneFreeForm = false,
  featureId = 'premium-feature',
  useFreePage = false
}) => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const { user } = useUser();
  const { isAdmin, loading: companyLoading, company } = useCompany();
  
  const { 
    isPremium, 
    loading: subscriptionLoading, 
    userRole,
    freeFormRemaining,
    subscriptionTier
  } = useSubscription();
  
  const [loading, setLoading] = useState(true);
  const [showFreeUsageAlert, setShowFreeUsageAlert] = useState(false);
  const alertId = `free-form-alert-${user?.id || 'unknown'}`;

  // Don't show "free form" messaging for premium users, enterprise/managers with company tier, or Admin/Owner
  const hasPremiumOrNonFreeTier =
    isAdmin ||
    isPremium ||
    (subscriptionTier && subscriptionTier !== 'free') ||
    (company?.subscriptionTier && company.subscriptionTier !== 'free');

  useEffect(() => {
    if (subscriptionLoading || !user?.id || !allowOneFreeForm) return;

    // Never show the free-form alert for premium, enterprise, or admin users
    if (hasPremiumOrNonFreeTier) {
      setShowFreeUsageAlert(false);
      alertShownMap.delete(alertId);
      return;
    }

    // Only show the alert if free forms are enabled and user has 0 or 1 remaining
    if (typeof freeFormRemaining === 'number' && freeFormRemaining <= 1) {
      if (!alertShownMap.get(alertId)) {
        setShowFreeUsageAlert(true);
        alertShownMap.set(alertId, true);
      }
    } else {
      setShowFreeUsageAlert(false);
      alertShownMap.delete(alertId);
    }
    
    return () => {
      alertShownMap.delete(alertId);
    };
  }, [user?.id, alertId, allowOneFreeForm, freeFormRemaining, subscriptionLoading, hasPremiumOrNonFreeTier]);

  useEffect(() => {
    if (!subscriptionLoading) {
      setLoading(false);
    }
  }, [subscriptionLoading]);

  if (loading || subscriptionLoading || companyLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Allow access if: premium, or (free-form allowed and has remaining), or (free-form allowed and editing existing form), or Admin/Owner
  const canAccess =
    isAdmin ||
    isPremium ||
    (allowOneFreeForm && typeof freeFormRemaining === 'number' && freeFormRemaining > 0) ||
    (allowOneFreeForm && !!formId);

  if (canAccess) {
    return (
      <>
        {/* Show free-form alert only for actual free-tier users (not premium, enterprise, or Admin/Owner) */}
        {showFreeUsageAlert && !hasPremiumOrNonFreeTier && (
          <Alert 
            severity={typeof freeFormRemaining === 'number' && freeFormRemaining > 0 ? "info" : "warning"} 
            sx={{ mb: 3 }}
            onClose={() => {
              setShowFreeUsageAlert(false);
              alertShownMap.delete(alertId);
            }}
          >
            {typeof freeFormRemaining === 'number' && freeFormRemaining > 0 
              ? "You're using your free form. After this, you'll need to upgrade to premium to create additional forms."
              : "You've used your free form. Upgrade to premium to create unlimited forms."
            }
          </Alert>
        )}
        
        {children}
      </>
    );
  }

  return (
    <Container maxWidth="md">
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          textAlign: 'center'
        }}
      >
        <Paper 
          elevation={3}
          sx={{
            maxWidth: 600,
            width: '100%',
            p: { xs: 3, sm: 5 },
            borderRadius: 2
          }}
        >
          <Box 
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              width: 70,
              height: 70,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}
          >
            <LockIcon fontSize="large" />
          </Box>
          
          <Typography variant="h4" component="h1" gutterBottom>
            {allowOneFreeForm && freeFormRemaining === 0
              ? "Free Form Limit Reached" 
              : "Premium Feature"
            }
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph>
            {allowOneFreeForm && freeFormRemaining === 0
              ? "You've already created your free form. Upgrade to premium to create unlimited forms." 
              : "This feature is part of our premium offering."
            }
          </Typography>
          
          <Divider sx={{ my: 3 }} />
          
          <Stack spacing={3} sx={{ mt: 4 }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/payment-upgrade')}
              startIcon={<StarsIcon />}
            >
              Upgrade to Premium
            </Button>
            
            <Button 
              variant="outlined" 
              onClick={() => navigate('/home')}
            >
              Return to Home
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
};

export default RequireSubscription;
